import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { addFollowUp, listFollowUps } from '../../../api/leads';
import { FollowUpType } from '../../../types/lead';

const TYPES: FollowUpType[] = ['CALL', 'EMAIL', 'WHATSAPP', 'OFFICE_VISIT', 'SMS', 'ZOOM', 'NOTE', 'OTHER'];

export const FollowUpsTab = ({ leadId }: { leadId: number }) => {
  const queryClient = useQueryClient();
  const [type, setType] = useState<FollowUpType>('CALL');
  const [summary, setSummary] = useState('');
  const [outcome, setOutcome] = useState('');
  const [nextFollowUpAt, setNextFollowUpAt] = useState('');

  const followUpsQuery = useQuery({
    queryKey: ['follow-ups', leadId],
    queryFn: () => listFollowUps(leadId),
  });

  const addMutation = useMutation({
    mutationFn: () =>
      addFollowUp(leadId, {
        type,
        summary,
        outcome: outcome || undefined,
        nextFollowUpAt: nextFollowUpAt ? new Date(nextFollowUpAt).toISOString() : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['follow-ups', leadId] });
      queryClient.invalidateQueries({ queryKey: ['lead-timeline', leadId] });
      setSummary('');
      setOutcome('');
      setNextFollowUpAt('');
    },
  });

  return (
    <Box sx={{ mt: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Log a follow-up
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Type"
              size="small"
              sx={{ width: 160 }}
              value={type}
              onChange={(e) => setType(e.target.value as FollowUpType)}
            >
              {TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Next follow-up (optional)"
              type="datetime-local"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={nextFollowUpAt}
              onChange={(e) => setNextFollowUpAt(e.target.value)}
            />
          </Stack>
          <TextField
            label="Summary"
            multiline
            minRows={2}
            fullWidth
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
          />
          <TextField
            label="Outcome (optional)"
            fullWidth
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
          />
          <Button
            variant="contained"
            sx={{ alignSelf: 'flex-start' }}
            disabled={!summary.trim() || addMutation.isPending}
            onClick={() => addMutation.mutate()}
          >
            {addMutation.isPending ? 'Saving…' : 'Add follow-up'}
          </Button>
        </Stack>
      </Paper>

      <Stack spacing={1.5}>
        {followUpsQuery.data?.map((f) => (
          <Paper key={f.id} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2">
              {f.type} — {f.createdBy.firstName} {f.createdBy.lastName}
            </Typography>
            <Typography variant="body2">{f.summary}</Typography>
            {f.outcome && (
              <Typography variant="caption" color="text.secondary">
                Outcome: {f.outcome}
              </Typography>
            )}
            <Typography variant="caption" display="block" color="text.secondary">
              {new Date(f.createdAt).toLocaleString()}
            </Typography>
          </Paper>
        ))}
        {followUpsQuery.data?.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No follow-ups logged yet.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
